import { proxy, ref } from 'valtio';

import { FEN_STARTING_POSITION } from '@libs/chess/constants';
import { parseFen } from '@libs/chess/parseFen';
import { isPlayableFen } from '@libs/chess/validateFen';

import { ITimeControl, ICatalogGroup, ICatalogPosition } from '@features/game/ui/PopupControls';

import { DEFAULT_SETTINGS } from '../../contrants';
import { CATALOG } from '../../data/positionsCatalog';
import { IGameSettings, IGameSettingsTime } from '../../types';

export class ComputerSettingsState {
  constructor(initial: IGameSettings = DEFAULT_SETTINGS) {
    this.elo = initial.elo;
    this.fen = initial.fenManual;
    this.positionsTab = initial.fenMode;
    this.color = initial.color;
    this.white = this.toTimeControl(initial.white);
    this.black = this.toTimeControl(initial.black);
    this.positionsTab = initial.fenMode;
    this.positionsCatalog = ref(CATALOG);
    this.positionsSelected =
      initial.fenCatalog !== FEN_STARTING_POSITION
        ? initial.fenCatalog
        : (CATALOG[0]?.positions[0]?.value ?? FEN_STARTING_POSITION);
    this.displayPositionsOnMobile = this.positionsTab === 'catalog';
  }

  elo: number;
  fen?: string;
  color: 'white' | 'black' | 'alternate';
  white: ITimeControl;
  black: ITimeControl;
  displayPositionsOnMobile: boolean;
  positionsTab: 'manual' | 'catalog';
  positionsCatalog: ICatalogGroup[];
  positionsSelected: string;
  get positionsSelectedColor() {
    return parseFen(this.positionsSelected)!.color;
  }
  get canPlay() {
    if (this.positionsTab === 'catalog') return true;
    return isPlayableFen(this.fen);
  }

  getSettings(): IGameSettings {
    if (!this.fen) {
      throw new Error('No FEN provided');
    }

    return {
      color: this.color,
      elo: this.elo,
      fenManual: this.fen,
      fenCatalog: this.positionsSelected,
      fenMode: this.positionsTab,
      white: this.toCfgTime(this.white),
      black: this.toCfgTime(this.black),
    };
  }

  private toTimeControl(t?: IGameSettingsTime): ITimeControl {
    return {
      initialMin: t ? Math.max(Math.floor(t.timeInitialSec / 60), 1) : 5,
      additionalSec: t ? t.timeAddSec : 5,
      noTime: !t,
    };
  }

  private toCfgTime(t: ITimeControl): IGameSettingsTime {
    return {
      timeInitialSec: t.initialMin * 60,
      timeAddSec: t.additionalSec,
      noTimeControl: t.noTime,
    };
  }
}

export class ComputerSettingsStore {
  constructor(
    initial: IGameSettings = DEFAULT_SETTINGS,
    private _onPlay?: (cfg: IGameSettings) => void,
  ) {
    this.state = proxy(new ComputerSettingsState(initial));
  }

  state: ComputerSettingsState;
  keepUpToDate(onPlay?: (cfg: IGameSettings) => void) {
    this._onPlay = onPlay;
  }

  onEloChange = (elo: number) => {
    this.state.elo = elo;
  };

  onColorChange = (o: { value: ComputerSettingsState['color'] }) => {
    this.state.color = o.value;
  };

  onWhiteTimeControl = (t: ITimeControl) => {
    this.state.white = t;
  };

  onBlackTimeControl = (t: ITimeControl) => {
    this.state.black = t;
  };

  onDisplayPositionsChange = (d: boolean) => {
    this.state.displayPositionsOnMobile = d;
  };

  onPositionsTab = (o: { value: ComputerSettingsState['positionsTab'] }) => {
    this.state.positionsTab = o.value;
  };

  onFenChange = (fen?: string) => {
    this.state.fen = fen;
  };

  onPositionChange = (o: ICatalogPosition) => {
    this.state.positionsSelected = o.value;
  };

  onPlay = () => {
    this._onPlay?.(this.state.getSettings());
  };
}
